import { Edit, MoreHoriz, Reply, TagFaces } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { withReaction } from "components/hoc";
import withForwardMessage from "components/hoc/withForwardMessage";
import { QuoteIcon } from "components/icons";
import { FormatConstant, KeyConstant, SystemConstant } from "const";
import React, { memo, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { ConversationActions } from "redux-store";
import { getInteractor } from "services/local.service";
import StringFormat from "string-format";
import { StorageUtil } from "utils";
import { getMessageFileList } from "utils/message.utils";
import { useConversationContext } from "../ConversationContext";
import ChatActionMenu from "./ChatActionMenu";

const Reaction = withReaction(TagFaces);

const ChatAction = ({ isShow, isThreadMode, message, onEditMessage, onForward, isThreadSetting, isSendErr }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { groupDetail } = useConversationContext();

  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const isMine = message.senderId === accountId;
  const isExistMessage =
    false ===
    Boolean(message.isDeleteAll || message.sendType === SystemConstant.SEND_TYPE.notFound || message.isDeleteOnlyMe);

  const [menuAnchor, setMenuAnchorEl] = useState();
  const [isShowForwardOption, setIsShowForwardOption] = useState(
    !SystemConstant.ARR_CALLING_TYPES.includes(message.sendType),
  );

  const handleShowAction = event => setMenuAnchorEl(event.currentTarget);

  const handleClickEditing = () => {
    setMenuAnchorEl();
    onEditMessage();
  };

  const handleShowForward = async () => {
    const multipleFile = getMessageFileList(message);
    const successFile = await getInteractor(prefixKey).LocalFileService.find({
      id: multipleFile.map(item => item.attachmentId),
      status: SystemConstant.UPLOAD_STATUS.success,
    });

    const isSendFileSuccess = Array.isArray(successFile) && successFile.length > 0;
    setIsShowForwardOption(Boolean(isSendFileSuccess && isExistMessage));
  };

  const handleQuote = () => {
    dispatch(
      ConversationActions.conversationSet({
        quoteMessage: message,
      }),
    );
  };

  useEffect(() => {
    return () => setMenuAnchorEl();
  }, []);

  useEffect(() => {
    if (message && SystemConstant.ARR_CALLING_TYPES.includes(message.sendType)) {
      setIsShowForwardOption(false);
    } else if (message && Object.values(SystemConstant.MEDIA_SEND_TYPE).includes(message.sendType)) {
      handleShowForward();
    } else {
      setIsShowForwardOption(isExistMessage && !isSendErr);
    }
  }, [message]);

  const isBot = Boolean(groupDetail.isBot);
  const [isShowCreatedTime, isNormalMessage] = useMemo(() => {
    const isCallingMessage = SystemConstant.ARR_CALLING_TYPES.includes(message.sendType);
    const isNormalMessage = !isCallingMessage && isExistMessage && !isSendErr;

    return [!isCallingMessage, isNormalMessage];
  }, [message]);

  return (
    <Box
      className={clsx(classes.menuAndTimer, {
        [classes.menuAndTimerReceived]: !isMine,
      })}
    >
      {isShow && (
        <>
          <Stack direction={isMine ? "row" : "row-reverse"}>
            {isShowForwardOption && (
              <IconButton className={classes.chatMenuButton} onClick={() => onForward({ message })}>
                <Reply className={classes.replyIcon} />
              </IconButton>
            )}

            <IconButton className={classes.chatMenuButton} onClick={handleShowAction}>
              <MoreHoriz className={classes.chatMenuIcon} />
            </IconButton>

            {isNormalMessage && (
              <Reaction
                className={isBot ? "hidden" : classes.chatMenuButton}
                id={StringFormat(FormatConstant.FM_OPEN_EMOJI_BUTTON_ID, message.id)}
                message={message}
                IconProps={{ sx: { width: "20px", height: "20px" } }}
              />
            )}

            {isNormalMessage && (
              <IconButton className={classes.chatMenuButton} onClick={handleQuote}>
                <QuoteIcon />
              </IconButton>
            )}
          </Stack>

          {isShowCreatedTime && (
            <Stack direction={isMine ? "row" : "row-reverse"} className={classes.messageSentTimeWrapper}>
              <Typography component="span" className={clsx("medium-sm-txt", classes.messageSentTime)}>
                {message.sentTime}
              </Typography>

              {message.isEdited && <Edit className={classes.editedMessage} />}
            </Stack>
          )}
        </>
      )}

      <ChatActionMenu
        menuAnchor={menuAnchor}
        message={message}
        isThreadMode={isThreadMode}
        onEditMessage={handleClickEditing}
        onClose={() => setMenuAnchorEl()}
        isThreadSetting={isThreadSetting}
        isSendErr={isSendErr}
      />
    </Box>
  );
};

export default memo(withForwardMessage(ChatAction));

const useStyles = makeStyles(theme => ({
  messageSentTimeWrapper: {
    position: "absolute",
    top: -8,
    display: "flex",
    alignItems: "center",
  },

  messageSentTime: {
    color: "#666666",
    flexShrink: 0,
    marginLeft: 4,
    lineHeight: "1rem",
  },

  editedMessage: {
    color: theme.palette.grey[200],
    width: 12,
    height: 16,
    marginLeft: 4,
  },

  menuAndTimer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-end",
    minWidth: 12,
    marginLeft: 10,
    marginRight: 10,
    position: "relative",
  },

  menuAndTimerReceived: {
    alignItems: "flex-start",
    bottom: 2,
  },

  chatMenuIcon: {
    width: 20,
    height: 20,
  },

  chatMenuButton: {
    width: 32,
    height: 32,
    minHeight: 10,
    minWidth: 10,
    padding: 4,
  },

  replyIcon: {
    transform: "rotateY(180deg)",
  },
}));
